import React, { useEffect, useMemo, useState } from "react";

import { Alert, MenuItem, Select, Stack } from "@mui/material";

import { DEV_MODE } from "Constant/app/process";

import {
    getGoogleDmaAuthUrl,
    postAuthorizeGoogleDma,
} from "helpers/destinations/googleDma";

import LoginButton from "components/molecules/LoginButton";

import {
    DEV_MODE_MOCK_AUTHORIZE_RESPONSE,
    DEV_MODE_MOCK_LOGIN_PARAMS,
    normalizeAccountTree,
} from "./GoogleAdsConversion.helpers";

const GoogleAdsConversion = ({
    type,
    setGoogleAdsData,
    setGoogleAdsAuthenticated,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const [accounts, setAccounts] = useState([]);
    const [partnerInstallId, setPartnerInstallId] = useState(null);

    const [selectedAccountId, setSelectedAccountId] = useState("");
    const [selectedChildId, setSelectedChildId] = useState("");

    const selectedTopAccount = useMemo(
        () =>
            accounts.find((account) => account.accountId === selectedAccountId),
        [accounts, selectedAccountId],
    );

    const childOptions = useMemo(
        () => selectedTopAccount?.children ?? [],
        [selectedTopAccount],
    );
    const hasChildren = childOptions.length > 0;

    const operatingAccount = useMemo(() => {
        if (!selectedTopAccount) return undefined;
        if (hasChildren) {
            return childOptions.find(
                (child) => child.accountId === selectedChildId,
            );
        }
        return { ...selectedTopAccount, loginAccountId: null };
    }, [selectedTopAccount, hasChildren, childOptions, selectedChildId]);

    useEffect(() => {
        setIsLoading(false);
        setIsAuthenticated(false);
        setAccounts([]);
        setPartnerInstallId(null);
        setSelectedAccountId("");
        setSelectedChildId("");
        setGoogleAdsAuthenticated(false);
    }, [type, setGoogleAdsAuthenticated]);

    const applyAuthorizeResponse = (response) => {
        setAccounts(normalizeAccountTree(response?.data?.accounts));
        setPartnerInstallId(response?.data?.partner_install_id ?? null);

        if (response?.status) {
            setIsAuthenticated(true);
            setGoogleAdsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
        }
        setIsLoading(false);
    };

    const onLoginSuccess = async (data) => {
        setIsLoading(true);

        if (DEV_MODE) {
            applyAuthorizeResponse(DEV_MODE_MOCK_AUTHORIZE_RESPONSE);
            return;
        }

        const response = await postAuthorizeGoogleDma({ code: data.code });
        applyAuthorizeResponse(response);
    };

    useEffect(() => {
        setGoogleAdsData({
            selectedAccount: operatingAccount,
            partnerInstallId,
        });
    }, [operatingAccount, partnerInstallId, setGoogleAdsData]);

    return (
        <Stack spacing={2}>
            {isAuthenticated && (
                <>
                    <Select
                        label="Account"
                        value={selectedAccountId}
                        onChange={(e) => {
                            setSelectedAccountId(e.target.value);

                            setSelectedChildId("");
                        }}
                        disabled={!accounts.length}
                        displayEmpty
                        fullWidth
                        renderValue={(selected) => {
                            const account = accounts.find(
                                (item) => item.accountId === selected,
                            );
                            return account
                                ? `${account.name} (${account.accountId})`
                                : selected;
                        }}
                    >
                        {accounts.map((account) => (
                            <MenuItem
                                key={account.accountId}
                                value={account.accountId}
                            >
                                {account.children?.length
                                    ? `${account.name} (${account.accountId}) - MCC`
                                    : `${account.name} (${account.accountId})`}
                            </MenuItem>
                        ))}
                    </Select>

                    {hasChildren && (
                        <Select
                            label="Sub-account"
                            value={selectedChildId}
                            onChange={(e) => setSelectedChildId(e.target.value)}
                            displayEmpty
                            fullWidth
                            renderValue={(selected) => {
                                const child = childOptions.find(
                                    (item) => item.accountId === selected,
                                );
                                return child
                                    ? `${child.name} (${child.accountId})`
                                    : selected;
                            }}
                        >
                            {childOptions.map((child) => (
                                <MenuItem
                                    key={child.accountId}
                                    value={child.accountId}
                                >
                                    {`${child.name} (${child.accountId})`}
                                </MenuItem>
                            ))}
                        </Select>
                    )}

                    {!accounts.length && (
                        <Alert severity="warning">
                            This Google account has no Google Ads account
                            available. Connect a Google account that has access
                            to at least one Google Ads account.
                        </Alert>
                    )}
                </>
            )}

            {!isAuthenticated && (
                <LoginButton
                    code="google"
                    getAuthUrl={() => getGoogleDmaAuthUrl(type)}
                    getAuthUrlResponseTransform={(response) =>
                        response?.data?.url
                    }
                    onAuthUrlError={() => setIsLoading(false)}
                    paramNames={["code"]}
                    onSuccess={onLoginSuccess}
                    devSuccessParams={DEV_MODE_MOCK_LOGIN_PARAMS}
                    dataTransform={(params) => ({ code: params.code })}
                    logoSrc="/assets/images/google.png"
                    buttonText="Connect with Google"
                    disabled={false}
                    isLoadingExternal={isLoading}
                />
            )}
        </Stack>
    );
};

export default GoogleAdsConversion;
